import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Scissors,
  ChevronDown,
  ChevronUp,
  Search,
  HelpCircle,
  User,
  Building2,
  CreditCard,
  Calendar,
  Gift,
  Shield,
  MessageSquare,
  ArrowLeft,
  Mail,
  Phone
} from "lucide-react";
import AppFooter from "@/components/AppFooter";

const FAQPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [openItems, setOpenItems] = useState({});

  const categories = [
    { id: "all", label: "All Questions", icon: HelpCircle },
    { id: "getting-started", label: "Getting Started", icon: User },
    { id: "booking", label: "Booking", icon: Calendar },
    { id: "payments", label: "Payments", icon: CreditCard },
    { id: "membership", label: "Membership & Pricing", icon: Gift },
    { id: "stylists", label: "For Stylists & Barbers", icon: Scissors },
    { id: "owners", label: "For Salon Owners", icon: Building2 },
    { id: "security", label: "Security & Privacy", icon: Shield }
  ];

  const faqs = [
    // Getting Started
    {
      category: "getting-started",
      question: "What is MyHairChair?",
      answer: "MyHairChair is an all-in-one platform for beauty professionals and clients. It helps stylists, barbers, and salon owners manage bookings, accept payments, and grow their business, while clients can easily find and book appointments with talented professionals."
    },
    {
      category: "getting-started",
      question: "How do I create an account?",
      answer: "Click 'Get Started' on our homepage and choose your role: Client, Barber/Stylist, or Salon Owner. You can sign up with your email or use Google Sign-In for quick registration. It only takes about 2 minutes!"
    },
    {
      category: "getting-started",
      question: "What plans do you offer?",
      answer: "We offer two plans: Pro ($29.99/mo) for individual stylists with unlimited clients, CRM dashboard, Pro Mini-Site, loyalty rewards, and more. Elite ($49.99/mo) for salon owners includes everything in Pro plus staff management, rent collection, payroll tracking, and team analytics. Both plans include a 30-day FREE trial!"
    },
    {
      category: "getting-started",
      question: "Do you offer a free trial?",
      answer: "Yes! Both Pro and Elite plans include a 30-day free trial. You get full access to all features during the trial. After 30 days, you'll be charged the monthly rate. Cancel anytime during the trial at no cost."
    },

    // Booking
    {
      category: "booking",
      question: "How do I book an appointment?",
      answer: "Find a stylist using our search feature, view their profile and available services, select your preferred date and time, choose a payment method, and confirm your booking. You'll receive a confirmation email with all the details."
    },
    {
      category: "booking",
      question: "Can I reschedule or cancel my appointment?",
      answer: "Yes! You can reschedule or cancel appointments from your Client Dashboard. Please check the stylist's cancellation policy, as some may have specific requirements or fees for late cancellations."
    },
    {
      category: "booking",
      question: "How do appointment reminders work?",
      answer: "Appointment reminders are sent via email to both clients and stylists before scheduled appointments. SMS reminders are also available (coming soon). This feature is included in Pro and Elite plans."
    },
    {
      category: "booking",
      question: "Can I book multiple services at once?",
      answer: "Yes! When booking, you can select multiple services from the same stylist. The system will automatically calculate the total time and price for your appointment."
    },

    // Payments
    {
      category: "payments",
      question: "What payment methods are accepted?",
      answer: "We accept credit/debit cards through Stripe (available on all plans). Pro and Elite members can also accept payments via PayPal, Cash App, and Zelle. You can also choose to pay in person at the appointment."
    },
    {
      category: "payments",
      question: "Is my payment information secure?",
      answer: "Absolutely! We use Stripe for payment processing, which is PCI DSS Level 1 certified - the highest level of security in the payment industry. We never store your full card details on our servers."
    },
    {
      category: "payments",
      question: "When do stylists get paid?",
      answer: "Stripe payments are typically deposited into the stylist's bank account within 2-3 business days. For other payment methods (PayPal, Cash App, Zelle), funds are transferred directly to the stylist."
    },
    {
      category: "payments",
      question: "Are there any transaction fees?",
      answer: "Standard Stripe processing fees apply (approximately 2.9% + $0.30 per transaction). MyHairChair currently doesn't charge additional platform fees on any tier. This may change in the future as we grow."
    },

    // Membership & Pricing
    {
      category: "membership",
      question: "What are the differences between Pro and Elite plans?",
      answer: "Pro ($29.99/mo): Unlimited clients, CRM dashboard, appointment reminders, all payment methods (CashApp, Zelle, Cash, Stripe), loyalty program, calendar sync, Pro Mini-Site with custom URL, shareable QR code cards. Elite ($49.99/mo): Everything in Pro plus staff management, commission tracking, payroll & W-9 compliance, booth management, rent collection, team dashboard, analytics, and 0% platform fees."
    },
    {
      category: "membership",
      question: "Is there a free trial?",
      answer: "Yes! Both Pro and Elite plans come with a 30-day free trial. No credit card required to start. You can cancel anytime during the trial period without being charged."
    },
    {
      category: "membership",
      question: "Can I upgrade or downgrade my plan?",
      answer: "Yes, you can change your plan anytime from your dashboard. Upgrades take effect immediately, and downgrades take effect at the end of your current billing period."
    },
    {
      category: "membership",
      question: "What happens to my data if I cancel?",
      answer: "Your data is never deleted. If you cancel your subscription, you can still log in and view your client data and history. To reactivate, simply start a new subscription from your dashboard."
    },

    // For Stylists
    {
      category: "stylists",
      question: "How do I set up my stylist profile?",
      answer: "After registering as a stylist, go to your Dashboard and complete your profile. Add your professional type, services with prices, upload photos to your gallery, set your availability schedule, and configure your loyalty program."
    },
    {
      category: "stylists",
      question: "How do I manage my availability?",
      answer: "In your Stylist Dashboard, go to the 'Schedule' tab. You can set your working hours for each day, add break times, and block out specific dates for vacations or personal time."
    },
    {
      category: "stylists",
      question: "Can I share my booking page on social media?",
      answer: "Yes! Pro and Elite members can share their booking page directly to Instagram, Facebook, Twitter, and more. Your unique booking link can be added to your social media bio for easy client access."
    },
    {
      category: "stylists",
      question: "How does the loyalty program work?",
      answer: "You can set up a customizable loyalty program where clients earn points for each visit. Choose how many points per dollar spent and what rewards to offer (discounts, free services, etc.). This feature is available on Pro and Elite plans."
    },
    {
      category: "stylists",
      question: "How do I get more clients?",
      answer: "Optimize your profile with great photos, competitive pricing, and detailed service descriptions. Use our referral program to earn rewards for inviting other stylists. Share your booking link on social media. Pro members get priority listing in search results."
    },

    // For Salon Owners
    {
      category: "owners",
      question: "What features are available for salon owners?",
      answer: "Elite plan includes: booth/chair management, automated rent collection, multi-stylist dashboard, team analytics, profit/loss reports, tax summaries, and marketing tools. Perfect for managing multiple stylists or booth renters."
    },
    {
      category: "owners",
      question: "How does booth rent collection work?",
      answer: "Set up each booth with a rental rate (weekly or monthly). The system automatically tracks rent due and can send payment reminders to your booth renters. View all rent statuses from your Owner Dashboard."
    },
    {
      category: "owners",
      question: "Can I see analytics for my whole salon?",
      answer: "Yes! Elite plan includes comprehensive analytics showing total revenue, bookings per stylist, most popular services, busiest times, and more. Export reports for accounting or tax purposes."
    },
    {
      category: "owners",
      question: "Can booth renters have their own profiles?",
      answer: "Yes! Each booth renter can have their own stylist profile with independent bookings, services, and client base. As the owner, you can view aggregate data while they maintain their individual businesses."
    },

    // Security & Privacy
    {
      category: "security",
      question: "How is my personal information protected?",
      answer: "We use industry-standard encryption (SSL/TLS) for all data transmission. Personal information is stored securely and never shared with third parties without consent. We comply with data protection regulations."
    },
    {
      category: "security",
      question: "Can clients see my personal phone number?",
      answer: "No. Communication between clients and stylists happens through the platform. Your personal contact information is kept private unless you choose to share it."
    },
    {
      category: "security",
      question: "How do I delete my account?",
      answer: "You can request account deletion from your Profile Settings. For data portability, you can export your client list and booking history before deletion. Account deletion is permanent and cannot be undone."
    },
    {
      category: "security",
      question: "What happens if I suspect unauthorized access?",
      answer: "Immediately change your password and contact our support team. We recommend enabling Google Sign-In for added security. We monitor for suspicious activity and will alert you of any unusual login attempts."
    }
  ];

  const toggleItem = (index) => {
    setOpenItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = activeCategory === "all" || faq.category === activeCategory;
    const matchesSearch = searchQuery === "" || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/80 backdrop-blur-xl border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src="/images/myhairchair-logo.png" 
              alt="MyHairChair" 
              className="h-10 sm:h-12 md:h-14 w-auto"
            />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/portal">
              <Button variant="ghost" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <Link to="/login">
              <Button className="bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-medium">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="px-4 py-16 text-center">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 px-4 py-2 rounded-full mb-6">
            <HelpCircle className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 text-sm font-medium">Help Center</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            Frequently Asked{" "}
            <span className="bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent">
              Questions
            </span>
          </h1>
          <p className="text-gray-400 text-lg mb-8">
            Find answers to common questions about MyHairChair
          </p>

          {/* Search Bar */}
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              type="text"
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-stone-900 border-stone-700 text-white placeholder:text-gray-500 rounded-xl focus:border-amber-500"
            />
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="px-4 pb-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === cat.id
                    ? "bg-gradient-to-r from-amber-400 to-yellow-500 text-black"
                    : "bg-stone-800 text-gray-400 hover:bg-stone-700 hover:text-white"
                }`}
              >
                <cat.icon className="w-4 h-4" />
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Items */}
      <section className="px-4 pb-16">
        <div className="max-w-3xl mx-auto">
          {filteredFaqs.length === 0 ? (
            <div className="text-center py-12">
              <HelpCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No questions found matching your search.</p>
              <Button 
                variant="link" 
                onClick={() => { setSearchQuery(""); setActiveCategory("all"); }}
                className="text-amber-400 mt-2"
              >
                Clear filters
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredFaqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-stone-900 border border-stone-800 rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => toggleItem(index)}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-stone-800/50 transition-colors"
                  >
                    <span className="font-medium text-white pr-4">{faq.question}</span>
                    {openItems[index] ? (
                      <ChevronUp className="w-5 h-5 text-amber-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    )}
                  </button>
                  {openItems[index] && (
                    <div className="px-5 pb-5 pt-0">
                      <p className="text-gray-400 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Still Need Help Section */}
      <section className="px-4 pb-16">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-stone-900 to-stone-950 border border-amber-500/20 rounded-2xl p-8 text-center">
            <MessageSquare className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Still Have Questions?</h2>
            <p className="text-gray-400 mb-6">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="mailto:support@myhairchair.com">
                <Button className="bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-medium w-full sm:w-auto">
                  <Mail className="w-4 h-4 mr-2" />
                  Email Support
                </Button>
              </a>
              <Link to="/portal">
                <Button variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 w-full sm:w-auto">
                  <Phone className="w-4 h-4 mr-2" />
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="px-4 pb-16">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-xl font-bold text-white text-center mb-8">Popular Resources</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link to="/onboarding-guide" className="bg-stone-900 border border-stone-800 rounded-xl p-6 hover:border-amber-500/50 transition-colors group">
              <Calendar className="w-8 h-8 text-amber-400 mb-3" />
              <h4 className="font-semibold text-white group-hover:text-amber-400 transition-colors">Getting Started Guide</h4>
              <p className="text-gray-500 text-sm mt-1">Learn how to set up your profile</p>
            </Link>
            <Link to="/membership" className="bg-stone-900 border border-stone-800 rounded-xl p-6 hover:border-amber-500/50 transition-colors group">
              <Gift className="w-8 h-8 text-amber-400 mb-3" />
              <h4 className="font-semibold text-white group-hover:text-amber-400 transition-colors">Pricing Plans</h4>
              <p className="text-gray-500 text-sm mt-1">Compare Free, Pro, and Elite</p>
            </Link>
            <Link to="/referral-program" className="bg-stone-900 border border-stone-800 rounded-xl p-6 hover:border-amber-500/50 transition-colors group">
              <Gift className="w-8 h-8 text-amber-400 mb-3" />
              <h4 className="font-semibold text-white group-hover:text-amber-400 transition-colors">Referral Program</h4>
              <p className="text-gray-500 text-sm mt-1">Earn rewards for inviting friends</p>
            </Link>
          </div>
        </div>
      </section>

      <AppFooter />
    </div>
  );
};

export default FAQPage;
